/**
 * Formulario de configuración de NAP
 * Permite configurar splitters, puertos y conexiones
 * @module components/forms/NAPConfigForm
 */

import { useState } from 'react'
import { Plus, Trash2, Box, GitBranch, AlertTriangle, Cable, Circle } from 'lucide-react'
import { listFibrasParaPicker } from '../../utils/cableFibrasPicker'
import { cableLabelForSelect, cableLabelShort } from '@/utils/cableDisplayLabel'
import { SplitterConnectionEditor, SplitterSalida } from './SplitterConnectionEditor'
import { useFiberStore } from '@/stores'
import { mutarExtremoFibra, liberarFibraPorExtremo } from '@/services/connectionService'
import { cableEndpointAtNode } from '@/utils/cableEndpoints'

// Colores TIA-598
const COLORES_TIA598 = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

const getColorHex = (color: string) => COLORES_TIA598.find(c => c.codigo === color)?.hex || '#CCCCCC'

// Tipos
type RatioSplitter = '1:2' | '1:4' | '1:8' | '1:16' | '1:32' | '1:64'
type RatioDesbalanceado = '90/10' | '85/15' | '80/20' | '70/30' | '60/40'
type TipoSplitter = 'balanceado' | 'desbalanceado'
type EstadoPuerto = 'libre' | 'ocupado' | 'reservado' | 'dañado'
type TipoCaja = 'NAP aérea' | 'NAP pedestal' | 'NAP mural' | 'NAP subterránea'

interface SplitterPuertoDesbalanceado {
  puerto: number
  porcentaje: number
  atenuacion: number
}

// Entrada del splitter - puede venir de un cable o de otro splitter (cascada)
interface SplitterEntrada {
  tipoEntrada: 'cable' | 'splitter'
  // Si tipoEntrada === 'cable'
  cableId?: string
  cableNombre?: string
  bufferNum?: number
  bufferColor?: string
  fibraNum?: number
  fibraColor?: string
  fibraId?: string
  // Si tipoEntrada === 'splitter' (cascada)
  splitterOrigenId?: string
  puertoSalidaOrigen?: number
}

// Mantener compatibilidad con el tipo anterior
interface SplitterEntradaFibra extends Omit<SplitterEntrada, 'tipoEntrada'> {
  cableId: string
  fibraNum: number
  fibraColor: string
  fibraId: string
}

// Salida del splitter hacia cable, puerto NAP o otro splitter
interface SplitterSalidaDestino {
  puerto: number
  estado: 'libre' | 'conectado' | 'reservado'
  destino?: {
    tipo: 'cable' | 'puerto_nap' | 'splitter'
    cableId?: string
    cableNombre?: string
    fibraId?: string
    bufferNum?: number
    fibraNum?: number
    fibraColor?: string
    puertoNAP?: number
    splitterDestinoId?: string
  }
}

export interface Splitter {
  id: string
  tipoSplitter: TipoSplitter
  ratio: RatioSplitter
  ratioDesbalanceado?: RatioDesbalanceado
  puertoEntrada: number
  puertosActivos: number[]
  atenuacion: number
  puertosDesbalanceados?: SplitterPuertoDesbalanceado[]
  // Entrada del splitter (puede ser desde cable o desde otro splitter)
  entrada?: SplitterEntrada
  // Mantener compatibilidad con versión anterior
  entradaFibra?: SplitterEntradaFibra
  // Destinos de salidas
  salidas?: SplitterSalidaDestino[]
}

// Cable con estructura de fibras para selección
interface CableConFibras {
  id: string
  codigo?: string
  nombre?: string
  tipo: string
  numeroFibras: number
  tieneBuffers: boolean
  buffers?: Array<{
    numero: number
    color: string
    fibras: Array<{
      numero: number
      color: string
      estado: string
      fibraId: string
    }>
  }>
  fibras?: Array<{
    numero: number
    color: string
    estado: string
    fibraId: string
  }>
}

interface PuertoNAP {
  numero: number
  estado: EstadoPuerto
  clienteId?: string
  clienteNombre?: string
  fibraOrigen?: string
  splitterId?: string
  potencia?: number
  fechaInstalacion?: string
  notas?: string
  // Conexión con cable DROP
  cableDrop?: {
    cableId: string
    cableNombre?: string
    fibraId?: string
    fibraNum?: number
    fibraColor?: string
  }
}

interface NAPConfig {
  tipoCaja: TipoCaja
  modelo: string
  fabricante?: string
  capacidad: number
  splitters: Splitter[]
  puertos: PuertoNAP[]
  fibraEntrada?: {
    cableId: string
    buffer?: number
    fibra: number
    color: string
  }
}

interface NAPConfigFormProps {
  config?: NAPConfig
  onChange: (config: NAPConfig) => void
  cablesConectados?: CableConFibras[]
  /** ID de esta NAP (si ya existe). Sin esto no se puede saber qué extremo de
   *  cada cable marcar como usado, así que las asignaciones de fibra a
   *  splitters no tocan Cable.fibras -- solo quedan en node.config. */
  nodeId?: string
}

const TIPOS_CAJA: TipoCaja[] = ['NAP aérea', 'NAP pedestal', 'NAP mural', 'NAP subterránea']
const RATIOS_SPLITTER: RatioSplitter[] = ['1:2', '1:4', '1:8', '1:16', '1:32', '1:64']
const RATIOS_DESBALANCEADO: RatioDesbalanceado[] = ['90/10', '85/15', '80/20', '70/30', '60/40']

const ATENUACION_SPLITTER: Record<RatioSplitter, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 14.0,
  '1:32': 17.5,
  '1:64': 21.0,
}

const ATENUACION_DESBALANCEADO: Record<RatioDesbalanceado, { cascada: number; local: number }> = {
  '90/10': { cascada: 0.5, local: 10.0 },
  '85/15': { cascada: 0.7, local: 8.2 },
  '80/20': { cascada: 1.0, local: 7.0 },
  '70/30': { cascada: 1.5, local: 5.2 },
  '60/40': { cascada: 2.2, local: 4.0 },
}

function coercePositiveInt(n: unknown): number | undefined {
  const v = Number(n)
  if (!Number.isFinite(v) || v < 1) return undefined
  return Math.floor(v)
}

/**
 * Coacciona puertos numéricos (API/JSON a veces devuelve strings) y elimina puerto_nap sin número válido.
 */
export function normalizeSplittersForUi(splitters: Splitter[]): Splitter[] {
  return splitters.map((s) => {
    let entrada = s.entrada ? { ...s.entrada } : undefined
    if (entrada?.tipoEntrada === 'splitter' && entrada.puertoSalidaOrigen != null) {
      const p = coercePositiveInt(entrada.puertoSalidaOrigen)
      entrada = { ...entrada, puertoSalidaOrigen: p }
    }

    const salidas = (s.salidas || []).map((sal) => {
      const puerto = coercePositiveInt(sal.puerto) ?? sal.puerto
      if (!sal.destino) {
        return { ...sal, puerto }
      }
      const d = { ...sal.destino }
      if (d.tipo === 'puerto_nap') {
        const n = coercePositiveInt(d.puertoNAP)
        if (n == null) {
          return { puerto, estado: 'libre' as const, destino: undefined }
        }
        d.puertoNAP = n
      }
      return { ...sal, puerto, destino: d }
    })

    return { ...s, entrada, salidas }
  })
}

/**
 * Si un splitter hijo toma entrada desde otro (cascada), marca la salida del padre como ocupada
 * (destino tipo splitter → hijo). Limpia marcas huérfanas.
 * Usa comparación numérica de puertos (evita fallo string "3" vs number 3).
 */
export function syncSplitterCascadeSalidas(splitters: Splitter[]): Splitter[] {
  const children = new Map<string, { parentId: string; port: number }>()
  for (const s of splitters) {
    if (s.entrada?.tipoEntrada === 'splitter' && s.entrada.splitterOrigenId) {
      const port = coercePositiveInt(s.entrada.puertoSalidaOrigen)
      if (port != null) {
        children.set(s.id, {
          parentId: s.entrada.splitterOrigenId,
          port,
        })
      }
    }
  }

  const stripped = splitters.map((s) => ({
    ...s,
    salidas: (s.salidas || []).map((sal) => {
      if (sal.destino?.tipo === 'splitter' && sal.destino.splitterDestinoId) {
        const link = children.get(sal.destino.splitterDestinoId)
        const stillValid =
          link &&
          link.parentId === s.id &&
          Number(link.port) === Number(sal.puerto)
        if (!stillValid) {
          return { puerto: sal.puerto, estado: 'libre' as const, destino: undefined }
        }
      }
      return { ...sal }
    }),
  }))

  const result = stripped.map((s) => ({ ...s, salidas: [...(s.salidas || [])] }))

  for (const [childId, { parentId, port }] of children) {
    const pi = result.findIndex((x) => x.id === parentId)
    if (pi < 0) continue
    const parent = result[pi]
    const salidas = [...(parent.salidas || [])]
    const portN = Number(port)
    const newSalida: SplitterSalidaDestino = {
      puerto: portN,
      estado: 'conectado',
      destino: { tipo: 'splitter', splitterDestinoId: childId },
    }
    const existingIdx = salidas.findIndex((x) => Number(x.puerto) === portN)
    if (existingIdx >= 0) {
      salidas[existingIdx] = newSalida
    } else {
      salidas.push(newSalida)
    }
    result[pi] = { ...parent, salidas }
  }

  return result
}

function applySplitterSync(splitters: Splitter[]): Splitter[] {
  return syncSplitterCascadeSalidas(normalizeSplittersForUi(splitters))
}

export function NAPConfigForm({ config, onChange, cablesConectados = [], nodeId }: NAPConfigFormProps) {
  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)

  /**
   * Marca u ocupa/libera, en Cable.fibras, el extremo de una fibra que toca
   * ESTA NAP -- para que asignar un splitter a una fibra deje de ser invisible
   * para el resto de la app (empalmes, otros splitters, el mapa). No bloquea
   * la UI: el config del splitter ya se guardó vía updateSplitter/updateConfig
   * antes de llamar esto; si la escritura al cable falla, solo queda
   * desincronizado el estado visual de esa fibra, no se pierde la config del
   * splitter.
   */
  const marcarFibraEnEsteNodo = (
    cableId: string | undefined,
    bufferNum: number | null | undefined,
    fibraNum: number | undefined,
    cambio:
      | { estado: 'derivado' | 'fusionado'; splitterConectado: { splitterId: string; tipoPuerto: 'entrada' | 'salida'; numeroPuerto?: number } }
      | { estado: 'libre' }
  ) => {
    if (!nodeId || !cableId || !fibraNum) return
    const cable = cables.find((c) => c.id === cableId)
    if (!cable) return
    const extremo = cableEndpointAtNode(cable as any, nodeId)
    if (!extremo) return

    const actualizado =
      cambio.estado === 'libre'
        ? liberarFibraPorExtremo(cable as any, extremo, bufferNum ?? null, fibraNum)
        : mutarExtremoFibra(cable as any, extremo, bufferNum ?? null, fibraNum, {
            estado: cambio.estado,
            conectadoA: null,
            splitterConectado: cambio.splitterConectado,
          })

    void updateCable(actualizado as any)
  }
  const getInitialConfig = (): NAPConfig => {
    const capacidad = config?.capacidad || 8
    const defaultPuertos = Array.from({ length: capacidad }, (_, i) => ({
      numero: i + 1,
      estado: 'libre' as EstadoPuerto,
    }))
    
    return {
      tipoCaja: config?.tipoCaja || 'NAP aérea',
      modelo: config?.modelo || '',
      fabricante: config?.fabricante,
      capacidad: capacidad,
      splitters: config?.splitters || [],
      puertos: config?.puertos || defaultPuertos,
      fibraEntrada: config?.fibraEntrada,
    }
  }
  
  const [localConfig, setLocalConfig] = useState<NAPConfig>(() => {
    const initial = getInitialConfig()
    const splitters = applySplitterSync(initial.splitters || [])
    return { ...initial, splitters }
  })
  const [editingSplitterId, setEditingSplitterId] = useState<string | null>(null)
  const [showSplitterEditor, setShowSplitterEditor] = useState(false)

  const updateConfig = (updates: Partial<NAPConfig>) => {
    const newConfig = { ...localConfig, ...updates }
    setLocalConfig(newConfig)
    onChange(newConfig)
  }

  const updateCapacidad = (capacidad: number) => {
    const puertos = Array.from({ length: capacidad }, (_, i) => {
      const existing = localConfig.puertos.find(p => p.numero === i + 1)
      return existing || { numero: i + 1, estado: 'libre' as EstadoPuerto }
    })
    updateConfig({ capacidad, puertos })
  }

  const addSplitter = (tipo: TipoSplitter = 'balanceado') => {
    const id = `SPL-${Date.now()}`
    const nuevoSplitter: Splitter = {
      id,
      tipoSplitter: tipo,
      ratio: '1:8',
      ratioDesbalanceado: tipo === 'desbalanceado' ? '80/20' : undefined,
      puertoEntrada: 1,
      puertosActivos: [],
      atenuacion: tipo === 'balanceado' 
        ? ATENUACION_SPLITTER['1:8'] 
        : ATENUACION_DESBALANCEADO['80/20'].local,
      puertosDesbalanceados: tipo === 'desbalanceado' ? [
        { puerto: 1, porcentaje: 80, atenuacion: ATENUACION_DESBALANCEADO['80/20'].cascada },
        { puerto: 2, porcentaje: 20, atenuacion: ATENUACION_DESBALANCEADO['80/20'].local },
      ] : undefined,
    }
    updateConfig({ splitters: applySplitterSync([...localConfig.splitters, nuevoSplitter]) })
  }

  const removeSplitter = (id: string) => {
    const splitter = localConfig.splitters.find((s) => s.id === id)
    if (splitter) {
      const entradaCableId = splitter.entrada?.cableId || splitter.entradaFibra?.cableId
      const entradaFibraNum = splitter.entrada?.fibraNum || splitter.entradaFibra?.fibraNum
      if (entradaCableId && entradaFibraNum) {
        marcarFibraEnEsteNodo(entradaCableId, splitter.entrada?.bufferNum ?? splitter.entradaFibra?.bufferNum, entradaFibraNum, { estado: 'libre' })
      }
      for (const sal of splitter.salidas || []) {
        if (sal.destino?.tipo === 'cable' && sal.destino.cableId && sal.destino.fibraNum) {
          marcarFibraEnEsteNodo(sal.destino.cableId, sal.destino.bufferNum, sal.destino.fibraNum, { estado: 'libre' })
        }
      }
    }
    updateConfig({
      splitters: applySplitterSync(localConfig.splitters.filter((s) => s.id !== id)),
    })
  }

  const updateSplitter = (id: string, updates: Partial<Splitter>) => {
    const newSplitters = localConfig.splitters.map(s => {
      if (s.id === id) {
        const updated = { ...s, ...updates }
        
        if (updates.ratio && s.tipoSplitter === 'balanceado') {
          updated.atenuacion = ATENUACION_SPLITTER[updates.ratio]
        }
        
        if (updates.ratioDesbalanceado && s.tipoSplitter === 'desbalanceado') {
          const ratio = updates.ratioDesbalanceado
          const atenuaciones = ATENUACION_DESBALANCEADO[ratio]
          updated.atenuacion = atenuaciones.local
          updated.puertosDesbalanceados = [
            { puerto: 1, porcentaje: parseInt(ratio.split('/')[0]), atenuacion: atenuaciones.cascada },
            { puerto: 2, porcentaje: parseInt(ratio.split('/')[1]), atenuacion: atenuaciones.local },
          ]
        }
        
        return updated
      }
      return s
    })
    updateConfig({ splitters: applySplitterSync(newSplitters) })
  }

  const updatePuerto = (numero: number, updates: Partial<PuertoNAP>) => {
    const newPuertos = localConfig.puertos.map(p => 
      p.numero === numero ? { ...p, ...updates } : p
    )
    updateConfig({ puertos: newPuertos })
  }

  // Guardar salidas del splitter desde el editor
  const handleSaveSplitterSalidas = (splitterId: string, salidas: SplitterSalida[]) => {
    const newSplitters = localConfig.splitters.map(s => {
      if (s.id === splitterId) {
        // Convertir las salidas del editor al formato interno
        const salidasConvertidas: SplitterSalidaDestino[] = salidas.map((salida) => {
          let destino: SplitterSalidaDestino['destino'] = undefined
          if (salida.destino) {
            const tipo = salida.destino.tipo as 'cable' | 'puerto_nap' | 'splitter'
            if (tipo === 'puerto_nap') {
              const n = coercePositiveInt(salida.destino.puertoNAP)
              if (n == null) {
                return { puerto: salida.puerto, estado: 'libre' as const, destino: undefined }
              }
              destino = {
                tipo: 'puerto_nap',
                puertoNAP: n,
              }
            } else {
              destino = {
                tipo,
                cableId: salida.destino.cableId,
                cableNombre: salida.destino.cableNombre,
                fibraId: (salida.destino as { fibraId?: string }).fibraId,
                bufferNum: salida.destino.bufferNum,
                fibraNum: salida.destino.fibraNum,
                fibraColor: salida.destino.fibraColor,
                splitterDestinoId: salida.destino.splitterDestinoId,
              }
            }
          }
          return {
            puerto: salida.puerto,
            estado: salida.estado,
            destino,
          }
        })
        return { ...s, salidas: salidasConvertidas }
      }
      return s
    })
    updateConfig({ splitters: applySplitterSync(newSplitters) })
    setShowSplitterEditor(false)
    setEditingSplitterId(null)
  }

  // Obtener el splitter que se está editando
  const editingSplitter = editingSplitterId ? localConfig.splitters.find(s => s.id === editingSplitterId) : null

  const getEstadoColor = (estado: EstadoPuerto) => {
    switch (estado) {
      case 'libre': return 'bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-400'
      case 'ocupado': return 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-400'
      case 'reservado': return 'bg-yellow-100 border-yellow-400 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-600 dark:text-yellow-400'
      case 'dañado': return 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-400'
    }
  }

  const puertosLibres = localConfig.puertos.filter(p => p.estado === 'libre').length
  const puertosOcupados = localConfig.puertos.filter(p => p.estado === 'ocupado').length
  const puertosDanados = localConfig.puertos.filter(p => p.estado === 'dañado').length

  const getValidationWarnings = () => {
    const warnings: string[] = []
    
    if (puertosDanados > 0) {
      warnings.push(`${puertosDanados} puerto(s) dañado(s) requieren atención`)
    }
    
    if (localConfig.splitters.length > 0) {
      const splittersSinSalidas = localConfig.splitters.filter(s => s.puertosActivos.length === 0)
      if (splittersSinSalidas.length > 0) {
        warnings.push(`${splittersSinSalidas.length} splitter(s) sin puertos activos configurados`)
      }
    }
    
    const atenuacionTotal = localConfig.splitters.reduce((acc, s) => acc + s.atenuacion, 0)
    if (atenuacionTotal > 25) {
      warnings.push(`Atenuación total (${atenuacionTotal.toFixed(1)} dB) excede el límite recomendado de 25 dB`)
    }
    
    return warnings
  }

  const warnings = getValidationWarnings()

  return (
    <div className="space-y-6">
      {/* Advertencias de validación */}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200 text-sm">Advertencias de configuración</p>
              <ul className="mt-1 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                {warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Información de la caja */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Caja</label>
          <select
            value={localConfig.tipoCaja}
            onChange={(e) => updateConfig({ tipoCaja: e.target.value as TipoCaja })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {TIPOS_CAJA.map(tipo => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Modelo</label>
          <input
            type="text"
            value={localConfig.modelo}
            onChange={(e) => updateConfig({ modelo: e.target.value })}
            placeholder="Ej: NAP-8SC"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fabricante</label>
          <input
            type="text"
            value={localConfig.fabricante || ''}
            onChange={(e) => updateConfig({ fabricante: e.target.value })}
            placeholder="Ej: Corning"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Capacidad (puertos)</label>
          <select
            value={localConfig.capacidad}
            onChange={(e) => updateCapacidad(parseInt(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {[4, 8, 12, 16, 24, 32].map(cap => (
              <option key={cap} value={cap}>{cap} puertos</option>
            ))}
          </select>
        </div>
      </div>

      {/* Splitters */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Splitters
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => addSplitter('balanceado')}
              className="text-sm px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Balanceado
            </button>
            <button
              type="button"
              onClick={() => addSplitter('desbalanceado')}
              className="text-sm px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-1"
              title="Para topologías en cascada (daisy-chain)"
            >
              <Plus className="w-3 h-3" /> Desbalanceado
            </button>
          </div>
        </div>

        {localConfig.splitters.length === 0 ? (
          <div className="text-center py-4 text-gray-500 border-2 border-dashed rounded-lg">
            <GitBranch className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">Sin splitters (paso directo)</p>
          </div>
        ) : (
          <div className="space-y-3">
            {localConfig.splitters.map((splitter) => {
              // Cable de entrada: puede persistir solo en entrada o solo en entradaFibra
              const entradaCableId = splitter.entradaFibra?.cableId || splitter.entrada?.cableId
              const cableSeleccionado = entradaCableId
                ? cablesConectados.find((c) => c.id === entradaCableId)
                : null
              
              const fibrasDisponibles = cableSeleccionado
                ? listFibrasParaPicker(cableSeleccionado)
                : []

              // Un splitter desbalanceado (tap) siempre tiene 2 salidas físicas: cascada y local.
              // `ratio` (1:2..1:64) solo aplica a splitters balanceados.
              const numSalidas = splitter.tipoSplitter === 'desbalanceado' ? 2 : parseInt(splitter.ratio.split(':')[1])

              return (
                <div 
                  key={splitter.id} 
                  className={`border rounded-lg p-3 dark:border-dark-600 ${
                    splitter.tipoSplitter === 'desbalanceado' 
                      ? 'bg-orange-50 dark:bg-orange-900/10' 
                      : 'bg-purple-50 dark:bg-purple-900/10'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                      splitter.tipoSplitter === 'desbalanceado'
                        ? 'bg-orange-200 text-orange-800 dark:bg-orange-800/30 dark:text-orange-300'
                        : 'bg-purple-200 text-purple-800 dark:bg-purple-800/30 dark:text-purple-300'
                    }`}>
                      {splitter.tipoSplitter === 'desbalanceado' ? '⚖️ Desbalanceado' : '⚡ Balanceado'}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSplitter(splitter.id)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-4 flex-wrap">
                    {splitter.tipoSplitter === 'balanceado' ? (
                      <>
                        <div>
                          <label className="text-xs text-gray-500">Ratio</label>
                          <select
                            value={splitter.ratio}
                            onChange={(e) => updateSplitter(splitter.id, { ratio: e.target.value as RatioSplitter })}
                            className="block w-24 mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                          >
                            {RATIOS_SPLITTER.map(ratio => (
                              <option key={ratio} value={ratio}>{ratio}</option>
                            ))}
                          </select>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Salidas:</span>
                          <span className="ml-1 font-medium">{numSalidas}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Atenuación:</span>
                          <span className="ml-1 font-medium text-orange-600">{splitter.atenuacion} dB</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs text-gray-500">Ratio Cascada/Local</label>
                          <select
                            value={splitter.ratioDesbalanceado || '80/20'}
                            onChange={(e) => updateSplitter(splitter.id, { ratioDesbalanceado: e.target.value as RatioDesbalanceado })}
                            className="block w-28 mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                          >
                            {RATIOS_DESBALANCEADO.map(ratio => (
                              <option key={ratio} value={ratio}>{ratio}</option>
                            ))}
                          </select>
                        </div>
                        <div className="text-xs space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="w-20 text-gray-500">P1 Cascada:</span>
                            <span className="font-medium text-blue-600">
                              {splitter.puertosDesbalanceados?.[0]?.porcentaje}% 
                              ({splitter.puertosDesbalanceados?.[0]?.atenuacion.toFixed(1)} dB)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-20 text-gray-500">P2 Local:</span>
                            <span className="font-medium text-orange-600">
                              {splitter.puertosDesbalanceados?.[1]?.porcentaje}% 
                              ({splitter.puertosDesbalanceados?.[1]?.atenuacion.toFixed(1)} dB)
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Selector de Entrada del Splitter */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-600">
                    <div className="flex items-center gap-2 mb-2">
                      <Cable className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">Entrada del Splitter</span>
                    </div>

                    {/* Selector de tipo de entrada */}
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => updateSplitter(splitter.id, { 
                          entrada: { tipoEntrada: 'cable' },
                          entradaFibra: undefined 
                        })}
                        className={`flex-1 px-2 py-1 text-xs rounded border ${
                          (!splitter.entrada || splitter.entrada.tipoEntrada === 'cable')
                            ? 'bg-blue-100 border-blue-400 text-blue-700'
                            : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        Desde Cable
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const cableId = splitter.entrada?.cableId || splitter.entradaFibra?.cableId
                          const fibraNum = splitter.entrada?.fibraNum || splitter.entradaFibra?.fibraNum
                          if (cableId && fibraNum) {
                            marcarFibraEnEsteNodo(cableId, splitter.entrada?.bufferNum ?? splitter.entradaFibra?.bufferNum, fibraNum, { estado: 'libre' })
                          }
                          updateSplitter(splitter.id, {
                            entrada: { tipoEntrada: 'splitter' },
                            entradaFibra: undefined
                          })
                        }}
                        disabled={localConfig.splitters.filter(s => s.id !== splitter.id).length === 0}
                        className={`flex-1 px-2 py-1 text-xs rounded border ${
                          splitter.entrada?.tipoEntrada === 'splitter'
                            ? 'bg-purple-100 border-purple-400 text-purple-700'
                            : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                        title={localConfig.splitters.filter(s => s.id !== splitter.id).length === 0 ? 'No hay otros splitters disponibles' : ''}
                      >
                        Desde Splitter (Cascada)
                      </button>
                    </div>
                    
                    {/* Entrada desde Cable */}
                    {(!splitter.entrada || splitter.entrada.tipoEntrada === 'cable') && (
                      cablesConectados.length === 0 ? (
                        <div className="text-xs text-gray-500 bg-gray-100 dark:bg-dark-700 p-2 rounded">
                          No hay cables conectados a esta NAP. Conecte un cable primero.
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {/* Selector de Cable */}
                          {/* Selector de Cable */}
                          <div>
                            <label className="text-xs text-gray-500">Cable de entrada</label>
                            <select
                              value={splitter.entrada?.cableId || splitter.entradaFibra?.cableId || ''}
                              onChange={(e) => {
                                const cableId = e.target.value
                                const cableAnteriorId = splitter.entrada?.cableId || splitter.entradaFibra?.cableId
                                const fibraAnteriorNum = splitter.entrada?.fibraNum || splitter.entradaFibra?.fibraNum
                                const fibraAnteriorId = splitter.entrada?.fibraId || splitter.entradaFibra?.fibraId
                                // Cambiar de cable (o limpiar) abandona la fibra que ya estaba elegida.
                                if (cableAnteriorId && fibraAnteriorId && fibraAnteriorNum && cableAnteriorId !== cableId) {
                                  marcarFibraEnEsteNodo(cableAnteriorId, splitter.entrada?.bufferNum ?? splitter.entradaFibra?.bufferNum, fibraAnteriorNum, { estado: 'libre' })
                                }
                                if (!cableId) {
                                  updateSplitter(splitter.id, { entrada: { tipoEntrada: 'cable' }, entradaFibra: undefined })
                                } else {
                                  const cable = cablesConectados.find(c => c.id === cableId)
                                  updateSplitter(splitter.id, {
                                    entrada: {
                                      tipoEntrada: 'cable',
                                      cableId,
                                      cableNombre: cable ? cableLabelShort(cable) : undefined,
                                      fibraNum: 1,
                                      fibraColor: 'azul',
                                      fibraId: ''
                                    },
                                    entradaFibra: {
                                      cableId,
                                      cableNombre: cable ? cableLabelShort(cable) : undefined,
                                      fibraNum: 1,
                                      fibraColor: 'azul',
                                      fibraId: ''
                                    }
                                  })
                                }
                              }}
                              className="block w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                            >
                              <option value="">Seleccionar cable...</option>
                              {cablesConectados.map(cable => (
                                <option key={cable.id} value={cable.id}>
                                  {cableLabelForSelect(cable)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Selector de Fibra */}
                          <div>
                            <label className="text-xs text-gray-500">Hilo/Fibra</label>
                            <select
                              value={splitter.entrada?.fibraId || splitter.entradaFibra?.fibraId || ''}
                              onChange={(e) => {
                                const fibraId = e.target.value
                                const fibra = fibrasDisponibles.find(f => f.fibraId === fibraId)
                                const currentCable = splitter.entrada?.cableId || splitter.entradaFibra?.cableId
                                if (fibra && currentCable) {
                                  const fibraAnteriorNum = splitter.entrada?.fibraNum || splitter.entradaFibra?.fibraNum
                                  const fibraAnteriorId = splitter.entrada?.fibraId || splitter.entradaFibra?.fibraId
                                  if (fibraAnteriorId && fibraAnteriorNum && fibraAnteriorId !== fibraId) {
                                    marcarFibraEnEsteNodo(currentCable, splitter.entrada?.bufferNum ?? splitter.entradaFibra?.bufferNum, fibraAnteriorNum, { estado: 'libre' })
                                  }
                                  marcarFibraEnEsteNodo(currentCable, fibra.bufferNum, fibra.fibraNum, {
                                    estado: 'derivado',
                                    splitterConectado: { splitterId: splitter.id, tipoPuerto: 'entrada' },
                                  })
                                  const cableNombre = splitter.entrada?.cableNombre || splitter.entradaFibra?.cableNombre
                                  updateSplitter(splitter.id, {
                                    entrada: {
                                      tipoEntrada: 'cable',
                                      cableId: currentCable,
                                      cableNombre,
                                      fibraId,
                                      fibraNum: fibra.fibraNum,
                                      fibraColor: fibra.fibraColor,
                                      bufferNum: fibra.bufferNum,
                                      bufferColor: fibra.bufferColor
                                    },
                                    entradaFibra: {
                                      cableId: currentCable,
                                      cableNombre,
                                      fibraId,
                                      fibraNum: fibra.fibraNum,
                                      fibraColor: fibra.fibraColor,
                                      bufferNum: fibra.bufferNum,
                                      bufferColor: fibra.bufferColor
                                    }
                                  })
                                }
                              }}
                              disabled={!splitter.entrada?.cableId && !splitter.entradaFibra?.cableId}
                              className="block w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600 disabled:opacity-50"
                            >
                            <option value="">Seleccionar fibra...</option>
                            {fibrasDisponibles.map(fibra => (
                              <option 
                                key={fibra.fibraId} 
                                value={fibra.fibraId}
                                disabled={fibra.estado !== 'libre'}
                              >
                                {fibra.bufferNum ? `B${fibra.bufferNum}-` : ''}H{fibra.fibraNum} ({fibra.fibraColor})
                                {fibra.estado !== 'libre' ? ` [${fibra.estado}]` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      )
                    )}

                    {/* Entrada desde otro Splitter (Cascada) */}
                    {splitter.entrada?.tipoEntrada === 'splitter' && (
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded">
                        <label className="text-xs text-purple-600 dark:text-purple-400 font-medium">Entrada desde Splitter</label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <select
                            value={splitter.entrada?.splitterOrigenId || ''}
                            onChange={(e) => {
                              const splitterOrigenId = e.target.value
                              updateSplitter(splitter.id, {
                                entrada: {
                                  tipoEntrada: 'splitter',
                                  splitterOrigenId,
                                  puertoSalidaOrigen: undefined
                                },
                                entradaFibra: undefined
                              })
                            }}
                            className="text-sm px-2 py-1 border rounded dark:bg-dark-700 dark:border-dark-600"
                          >
                            <option value="">Seleccionar splitter...</option>
                            {localConfig.splitters
                              .filter(s => s.id !== splitter.id)
                              .map((s, idx) => (
                                <option key={s.id} value={s.id}>
                                  Splitter {idx + 1} ({s.tipoSplitter === 'desbalanceado' ? (s.ratioDesbalanceado || '80/20') : s.ratio})
                                </option>
                              ))}
                          </select>
                          <select
                            value={splitter.entrada?.puertoSalidaOrigen || ''}
                            onChange={(e) => {
                              const puertoSalidaOrigen = parseInt(e.target.value)
                              updateSplitter(splitter.id, {
                                entrada: {
                                  ...splitter.entrada!,
                                  puertoSalidaOrigen
                                }
                              })
                            }}
                            disabled={!splitter.entrada?.splitterOrigenId}
                            className="text-sm px-2 py-1 border rounded dark:bg-dark-700 dark:border-dark-600 disabled:opacity-50"
                          >
                            <option value="">Puerto salida...</option>
                            {(() => {
                              const splitterOrigen = localConfig.splitters.find(s => s.id === splitter.entrada?.splitterOrigenId)
                              if (!splitterOrigen) return null
                              // Un splitter desbalanceado (tap) siempre tiene 2 salidas físicas.
                              const numSalidasOrigen = splitterOrigen.tipoSplitter === 'desbalanceado' ? 2 : parseInt(splitterOrigen.ratio.split(':')[1])
                              return Array.from({ length: numSalidasOrigen }, (_, i) => {
                                const salidaUsada = splitterOrigen.salidas?.find(s => s.puerto === i + 1 && s.estado === 'conectado')
                                return (
                                  <option key={i + 1} value={i + 1} disabled={!!salidaUsada}>
                                    S{i + 1} {salidaUsada ? '(ocupado)' : ''}
                                  </option>
                                )
                              })
                            })()}
                          </select>
                        </div>
                        {splitter.entrada?.splitterOrigenId && splitter.entrada?.puertoSalidaOrigen && (
                          <div className="mt-2 text-xs text-purple-700 dark:text-purple-300 flex items-center gap-1">
                            <GitBranch className="w-3 h-3" />
                            Cascada: Splitter {localConfig.splitters.findIndex(s => s.id === splitter.entrada?.splitterOrigenId) + 1} 
                            → Salida {splitter.entrada.puertoSalidaOrigen}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mostrar fibra seleccionada (desde cable) */}
                    {(!splitter.entrada || splitter.entrada.tipoEntrada === 'cable') && 
                     (splitter.entrada?.fibraId || splitter.entradaFibra?.fibraId) && (
                      <div className="mt-2 flex items-center gap-2 bg-green-50 dark:bg-green-900/20 p-2 rounded text-sm">
                        <Circle 
                          className="w-3 h-3" 
                          fill={getColorHex(splitter.entrada?.fibraColor || splitter.entradaFibra?.fibraColor || 'azul')} 
                          stroke={getColorHex(splitter.entrada?.fibraColor || splitter.entradaFibra?.fibraColor || 'azul')} 
                        />
                        <span className="text-green-700 dark:text-green-300">
                          Entrada: {splitter.entrada?.cableNombre || splitter.entradaFibra?.cableNombre} → 
                          {(splitter.entrada?.bufferNum || splitter.entradaFibra?.bufferNum) 
                            ? ` Buffer ${splitter.entrada?.bufferNum || splitter.entradaFibra?.bufferNum} (${splitter.entrada?.bufferColor || splitter.entradaFibra?.bufferColor})` 
                            : ''} 
                          {' '}Hilo {splitter.entrada?.fibraNum || splitter.entradaFibra?.fibraNum} ({splitter.entrada?.fibraColor || splitter.entradaFibra?.fibraColor})
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Configuración de Salidas del Splitter */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-600">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Salidas ({numSalidas})</span>
                      <span className="text-xs text-gray-500">
                        {splitter.salidas?.filter(s => s.estado === 'conectado').length || 0} conectadas
                      </span>
                    </div>
                    
                    {/* Lista de salidas con selectores */}
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {Array.from({ length: numSalidas }, (_, i) => {
                        const salidaNum = i + 1
                        const salida = splitter.salidas?.find(s => s.puerto === salidaNum)
                        const colorSalida = COLORES_TIA598[i % 12]
                        
                        // Obtener puertos NAP disponibles
                        const puertosNAPUsados = localConfig.splitters.flatMap((s) =>
                          (s.salidas || [])
                            .filter(
                              (sal) =>
                                sal.destino?.tipo === 'puerto_nap' &&
                                !(s.id === splitter.id && sal.puerto === salidaNum)
                            )
                            .map((sal) => sal.destino?.puertoNAP)
                            .filter((n): n is number => typeof n === 'number' && !Number.isNaN(n))
                        )
                        
                        // Todos los cables conectados al nodo (incl. TRONCAL) pueden recibir salida hacia fibra
                        const cablesParaSalida = cablesConectados
                        
                        // Función para actualizar la salida
                        const updateSalidaDestino = (tipo: 'libre' | 'puerto_nap' | 'cable', valor?: number | string) => {
                          const newSalidas: SplitterSalidaDestino[] = [...(splitter.salidas || [])]
                          const existingIdx = newSalidas.findIndex(s => s.puerto === salidaNum)
                          
                          let newSalida: SplitterSalidaDestino
                          
                          if (tipo === 'libre') {
                            newSalida = { puerto: salidaNum, estado: 'libre', destino: undefined }
                          } else if (tipo === 'puerto_nap') {
                            const napNum = coercePositiveInt(valor)
                            if (napNum == null) return
                            newSalida = {
                              puerto: salidaNum,
                              estado: 'conectado',
                              destino: { tipo: 'puerto_nap', puertoNAP: napNum }
                            }
                          } else {
                            // tipo === 'cable'
                            if (valor === '__INIT_CABLE__') {
                              newSalida = {
                                puerto: salidaNum,
                                estado: 'conectado',
                                destino: { tipo: 'cable' },
                              }
                            } else {
                              const raw = String(valor ?? '')
                              const sep = raw.indexOf('|FIBRA|')
                              const cableId = sep >= 0 ? raw.slice(0, sep) : raw
                              const fibraId = sep >= 0 ? raw.slice(sep + '|FIBRA|'.length) : ''
                              const cable = cablesConectados.find(c => c.id === cableId)
                              let fibraInfo: { bufferNum?: number; fibraNum: number; fibraColor: string } | null = null

                              if (cable && fibraId) {
                                if (cable.tieneBuffers && cable.buffers) {
                                  for (const buffer of cable.buffers) {
                                    const fibra = buffer.fibras.find(f => f.fibraId === fibraId)
                                    if (fibra) {
                                      fibraInfo = { bufferNum: buffer.numero, fibraNum: fibra.numero, fibraColor: fibra.color }
                                      break
                                    }
                                  }
                                }
                                if (!fibraInfo && cable.fibras) {
                                  const fibra = cable.fibras.find(f => f.fibraId === fibraId)
                                  if (fibra) {
                                    fibraInfo = { fibraNum: fibra.numero, fibraColor: fibra.color }
                                  }
                                }
                                if (!fibraInfo) {
                                  const fromPicker = listFibrasParaPicker(cable).find(f => f.fibraId === fibraId)
                                  if (fromPicker) {
                                    fibraInfo = {
                                      bufferNum: fromPicker.bufferNum,
                                      fibraNum: fromPicker.fibraNum,
                                      fibraColor: fromPicker.fibraColor,
                                    }
                                  }
                                }
                              }
                              
                              newSalida = {
                                puerto: salidaNum,
                                estado: 'conectado',
                                destino: {
                                  tipo: 'cable',
                                  cableId,
                                  cableNombre: cable ? cableLabelShort(cable) : undefined,
                                  fibraId: fibraId || undefined,
                                  bufferNum: fibraInfo?.bufferNum,
                                  fibraNum: fibraInfo?.fibraNum || 1,
                                  fibraColor: fibraInfo?.fibraColor || 'azul',
                                }
                              }
                            }
                          }
                          
                          // Liberar la fibra que esta salida tenía antes, si apuntaba a un cable/fibra
                          // distinto de la nueva -- si no, queda marcada como usada para siempre.
                          const destinoAnterior = salida?.destino
                          if (destinoAnterior?.tipo === 'cable' && destinoAnterior.cableId && destinoAnterior.fibraId) {
                            const mismoDestino =
                              newSalida.destino?.tipo === 'cable' &&
                              newSalida.destino.cableId === destinoAnterior.cableId &&
                              newSalida.destino.fibraId === destinoAnterior.fibraId
                            if (!mismoDestino) {
                              marcarFibraEnEsteNodo(destinoAnterior.cableId, destinoAnterior.bufferNum, destinoAnterior.fibraNum, { estado: 'libre' })
                            }
                          }
                          // Marcar la nueva fibra de destino, solo si de verdad se eligió una (no en el
                          // paso intermedio '__INIT_CABLE__', que todavía no tiene fibraId).
                          if (newSalida.destino?.tipo === 'cable' && newSalida.destino.fibraId && newSalida.destino.cableId) {
                            marcarFibraEnEsteNodo(newSalida.destino.cableId, newSalida.destino.bufferNum, newSalida.destino.fibraNum, {
                              estado: 'fusionado',
                              splitterConectado: { splitterId: splitter.id, tipoPuerto: 'salida', numeroPuerto: salidaNum },
                            })
                          }

                          if (existingIdx >= 0) {
                            newSalidas[existingIdx] = newSalida
                          } else {
                            newSalidas.push(newSalida)
                          }

                          updateSplitter(splitter.id, { salidas: newSalidas })
                        }
                        
                        const esCascada =
                          salida?.destino?.tipo === 'splitter' &&
                          !!salida.destino.splitterDestinoId
                        const childIdx = esCascada
                          ? localConfig.splitters.findIndex(
                              (s) => s.id === salida!.destino!.splitterDestinoId
                            )
                          : -1

                        return (
                          <div 
                            key={salidaNum}
                            className={`p-2 border rounded ${
                              esCascada
                                ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/25 dark:border-purple-600'
                                : salida?.estado === 'conectado' 
                                  ? 'bg-green-50 border-green-300 dark:bg-green-900/20' 
                                  : 'bg-gray-50 border-gray-200 dark:bg-dark-700'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {/* Indicador de salida */}
                              <div className="flex items-center gap-1 min-w-[50px]">
                                <Circle
                                  className="w-3 h-3"
                                  fill={getColorHex(colorSalida.codigo)}
                                  stroke={getColorHex(colorSalida.codigo)}
                                />
                                <span className="text-xs font-bold">S{salidaNum}</span>
                              </div>

                              {splitter.tipoSplitter === 'desbalanceado' && splitter.puertosDesbalanceados?.[i] && (
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                                    i === 0
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                      : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                  }`}
                                  title={`${i === 0 ? 'Cascada' : 'Local'}: ${splitter.puertosDesbalanceados[i].porcentaje}% (${splitter.puertosDesbalanceados[i].atenuacion.toFixed(1)} dB)`}
                                >
                                  {i === 0 ? 'Cascada' : 'Local'} {splitter.puertosDesbalanceados[i].porcentaje}%
                                </span>
                              )}

                              {esCascada ? (
                                <div className="flex flex-1 items-center gap-2 rounded border border-purple-200 dark:border-purple-700 bg-white/80 dark:bg-dark-800 px-2 py-1 text-xs text-purple-800 dark:text-purple-200">
                                  <GitBranch className="w-3 h-3 shrink-0" />
                                  <span>
                                    Cascada → Splitter {childIdx >= 0 ? childIdx + 1 : '?'}
                                    <span className="text-gray-500 dark:text-gray-400 ml-1 font-normal">
                                      (esta salida alimenta el splitter hijo)
                                    </span>
                                  </span>
                                </div>
                              ) : (
                                <>
                              {/* Selector de tipo de destino */}
                              <select
                                value={salida?.destino?.tipo || 'libre'}
                                onChange={(e) => {
                                  const tipo = e.target.value as 'libre' | 'puerto_nap' | 'cable'
                                  if (tipo === 'libre') {
                                    updateSalidaDestino('libre')
                                  } else if (tipo === 'puerto_nap') {
                                    const first = localConfig.puertos.find(
                                      (p) => !puertosNAPUsados.includes(p.numero)
                                    )
                                    const n = coercePositiveInt(first?.numero) ?? coercePositiveInt(localConfig.puertos[0]?.numero)
                                    if (n != null) updateSalidaDestino('puerto_nap', n)
                                  } else {
                                    updateSalidaDestino('cable', '__INIT_CABLE__')
                                  }
                                }}
                                className="text-xs px-1 py-0.5 border rounded bg-white dark:bg-dark-600 dark:border-dark-500"
                              >
                                <option value="libre">🔓 Libre</option>
                                <option value="puerto_nap">🔌 Puerto NAP</option>
                                <option value="cable">📡 Cable</option>
                              </select>
                              
                              {/* Solo destino Puerto NAP */}
                              {salida?.destino?.tipo === 'puerto_nap' && (
                                <select
                                  value={salida?.destino?.puertoNAP || ''}
                                  onChange={(e) => {
                                    const puertoNum = coercePositiveInt(e.target.value)
                                    if (puertoNum != null) {
                                      updateSalidaDestino('puerto_nap', puertoNum)
                                    } else {
                                      updateSalidaDestino('libre')
                                    }
                                  }}
                                  className="flex-1 text-xs px-1 py-0.5 border rounded bg-white dark:bg-dark-600 dark:border-dark-500"
                                >
                                  <option value="">Seleccionar puerto...</option>
                                  {localConfig.puertos.map(p => {
                                    const usado = puertosNAPUsados.includes(p.numero)
                                    const esActual = salida?.destino?.puertoNAP === p.numero
                                    return (
                                      <option 
                                        key={p.numero} 
                                        value={p.numero}
                                        disabled={usado && !esActual}
                                      >
                                        P{p.numero} {p.clienteNombre ? `(${p.clienteNombre})` : ''} {usado && !esActual ? '(ocupado)' : ''}
                                      </option>
                                    )
                                  })}
                                </select>
                              )}
                              
                              {salida?.destino?.tipo === 'cable' && (
                                <select
                                  value={
                                    salida.destino.cableId && salida.destino.fibraId
                                      ? `${salida.destino.cableId}|FIBRA|${salida.destino.fibraId}`
                                      : ''
                                  }
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      updateSalidaDestino('cable', e.target.value)
                                    } else {
                                      updateSalidaDestino('cable', '__INIT_CABLE__')
                                    }
                                  }}
                                  className="flex-1 text-xs px-1 py-0.5 border rounded bg-white dark:bg-dark-600 dark:border-dark-500"
                                >
                                  <option value="">Seleccionar fibra...</option>
                                  {cablesParaSalida.flatMap(cable =>
                                    listFibrasParaPicker(cable).map(f => {
                                      const id = `${cable.id}|FIBRA|${f.fibraId}`
                                      const pref = cableLabelForSelect(cable)
                                      const label = f.bufferNum != null
                                        ? `${pref} — B${f.bufferNum}-H${f.fibraNum} (${f.estado})`
                                        : `${pref} — H${f.fibraNum} (${f.estado})`
                                      return (
                                        <option key={id} value={id}>
                                          {label}
                                        </option>
                                      )
                                    })
                                  )}
                                </select>
                              )}
                                </>
                              )}
                              
                              {/* Indicador de estado */}
                              {salida?.estado === 'conectado' && (
                                <span className={esCascada ? 'text-purple-600 text-xs' : 'text-green-600 text-xs'}>✓</span>
                              )}
                            </div>
                            
                            {/* Resumen de conexión */}
                            {salida?.destino && (
                              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 pl-[58px]">
                                {salida.destino.tipo === 'puerto_nap' 
                                  ? `→ Puerto NAP #${salida.destino.puertoNAP}` 
                                  : salida.destino.tipo === 'cable'
                                    ? salida.destino.fibraId
                                      ? (() => {
                                          const cab = cablesConectados.find(
                                            (x) => x.id === salida.destino!.cableId
                                          )
                                          const cn = cab
                                            ? cableLabelShort(cab)
                                            : salida.destino.cableNombre || 'Cable'
                                          return `→ ${cn} ${salida.destino.bufferNum != null ? `B${salida.destino.bufferNum}-` : ''}H${salida.destino.fibraNum} (${salida.destino.fibraColor})`
                                        })()
                                      : '→ Selecciona cable y fibra de destino'
                                    : salida.destino.tipo === 'splitter'
                                      ? `→ Splitter hijo ${childIdx >= 0 ? childIdx + 1 : ''} (cascada)`
                                    : ''}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    
                    {/* Botones de acción rápida */}
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          const puertosConectados = new Set(
                            localConfig.splitters.flatMap((s) =>
                              (s.salidas || [])
                                .filter((sal) => sal.destino?.tipo === 'puerto_nap')
                                .map((sal) => sal.destino?.puertoNAP)
                                .filter((n): n is number => typeof n === 'number' && !Number.isNaN(n))
                            )
                          )
                          const puertosLibres = localConfig.puertos.filter(
                            p => !puertosConectados.has(p.numero)
                          )
                          
                          const newSalidas: SplitterSalidaDestino[] = [...(splitter.salidas || [])]
                          let puertoIdx = 0
                          
                          for (let i = 0; i < numSalidas && puertoIdx < puertosLibres.length; i++) {
                            const existingSalida = newSalidas.find(s => s.puerto === i + 1)
                            if (!existingSalida || existingSalida.estado !== 'conectado') {
                              const existingIdx = newSalidas.findIndex(s => s.puerto === i + 1)
                              const newSalida: SplitterSalidaDestino = {
                                puerto: i + 1,
                                estado: 'conectado',
                                destino: { tipo: 'puerto_nap', puertoNAP: puertosLibres[puertoIdx].numero }
                              }
                              if (existingIdx >= 0) {
                                newSalidas[existingIdx] = newSalida
                              } else {
                                newSalidas.push(newSalida)
                              }
                              puertoIdx++
                            }
                          }
                          updateSplitter(splitter.id, { salidas: newSalidas })
                        }}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        Auto-conectar a Puertos
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          updateSplitter(splitter.id, { salidas: [] })
                        }}
                        className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Limpiar Todo
                      </button>
                    </div>
                  </div>
                  
                  {splitter.tipoSplitter === 'desbalanceado' && (
                    <div className="mt-2 pt-2 border-t border-orange-200 dark:border-orange-800/30 text-xs text-gray-600 dark:text-gray-400">
                      💡 Ideal para topología daisy-chain: Puerto cascada continúa hacia siguiente NAP, puerto local alimenta splitter balanceado.
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Puertos de Salida */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Box className="w-4 h-4" />
            Puertos de Salida (hacia clientes)
          </h4>
          <div className="text-sm text-gray-500">
            <span className="text-green-600">{puertosLibres} libres</span>
            {' / '}
            <span className="text-blue-600">{puertosOcupados} ocupados</span>
          </div>
        </div>

        {/* Obtener cables DROP disponibles */}
        {(() => {
          const cablesDrop = cablesConectados.filter(c => 
            c.tipo === 'DROP' || c.tipo === 'ACOMETIDA' || c.tipo?.toLowerCase().includes('drop')
          )
          
          return (
            <div className="space-y-2">
              {localConfig.puertos.map((puerto) => {
                // Buscar si este puerto está conectado a alguna salida de splitter
                const conexionSplitter = localConfig.splitters.flatMap(spl => 
                  (spl.salidas || []).map(sal => ({
                    splitterId: spl.id,
                    splitterRatio: spl.ratio,
                    salidaNum: sal.puerto,
                    salidaColor: COLORES_TIA598[(sal.puerto - 1) % 12].codigo,
                    puertoNAP: sal.destino?.tipo === 'puerto_nap' ? sal.destino.puertoNAP : null
                  }))
                ).find(c => c.puertoNAP === puerto.numero)

                return (
                  <div
                    key={puerto.numero}
                    className={`p-3 border-2 rounded-lg ${getEstadoColor(puerto.estado)}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Número de puerto y estado */}
                      <div className="flex items-center gap-2 min-w-[80px]">
                        <span className="font-bold text-lg">P{puerto.numero}</span>
                        <select
                          value={puerto.estado}
                          onChange={(e) => updatePuerto(puerto.numero, { estado: e.target.value as EstadoPuerto })}
                          className="text-xs bg-transparent border rounded px-1 py-0.5"
                        >
                          <option value="libre">🟢</option>
                          <option value="ocupado">🔵</option>
                          <option value="reservado">🟡</option>
                          <option value="dañado">🔴</option>
                        </select>
                      </div>

                      {/* Conexión desde splitter (entrada al puerto) */}
                      <div className="flex items-center gap-1 min-w-[100px]">
                        {conexionSplitter ? (
                          <div className="flex items-center gap-1 text-xs bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded">
                            <Circle
                              className="w-3 h-3"
                              fill={getColorHex(conexionSplitter.salidaColor)}
                              stroke={getColorHex(conexionSplitter.salidaColor)}
                            />
                            <span className="text-purple-700 dark:text-purple-300 font-medium">
                              ← S{conexionSplitter.salidaNum}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Sin splitter</span>
                        )}
                      </div>

                      {/* Flecha */}
                      <span className="text-gray-400">→</span>

                      {/* Selector de cable DROP de salida */}
                      <div className="flex-1">
                        <select
                          value={puerto.cableDrop?.cableId || ''}
                          onChange={(e) => {
                            const cableId = e.target.value
                            if (!cableId) {
                              updatePuerto(puerto.numero, { cableDrop: undefined })
                            } else {
                              const cable = cablesConectados.find(c => c.id === cableId)
                              updatePuerto(puerto.numero, {
                                cableDrop: {
                                  cableId,
                                  cableNombre: cable ? cableLabelShort(cable) : undefined,
                                  fibraNum: 1,
                                  fibraColor: 'azul'
                                },
                                estado: 'ocupado'
                              })
                            }
                          }}
                          className="w-full text-xs px-2 py-1 border rounded bg-white dark:bg-dark-700 dark:border-dark-600"
                        >
                          <option value="">Seleccionar cable DROP...</option>
                          {cablesDrop.map(cable => (
                            <option key={cable.id} value={cable.id}>
                              🏠 {cableLabelForSelect(cable)}
                            </option>
                          ))}
                          {cablesConectados.filter(c => !cablesDrop.includes(c)).map(cable => (
                            <option key={cable.id} value={cable.id}>
                              📡 {cableLabelForSelect(cable)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Info del cliente si está ocupado */}
                      {puerto.estado === 'ocupado' && (
                        <input
                          type="text"
                          value={puerto.clienteNombre || ''}
                          onChange={(e) => updatePuerto(puerto.numero, { clienteNombre: e.target.value })}
                          placeholder="Cliente..."
                          className="w-32 px-2 py-1 text-xs border rounded bg-white dark:bg-dark-700"
                        />
                      )}
                    </div>

                    {/* Resumen de conexión */}
                    {(conexionSplitter || puerto.cableDrop) && (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        {conexionSplitter && (
                          <span className="bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                            Splitter S{conexionSplitter.salidaNum} ({conexionSplitter.salidaColor})
                          </span>
                        )}
                        {conexionSplitter && puerto.cableDrop && <span>→</span>}
                        {puerto.cableDrop && (
                          <span className="bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded">
                            {puerto.cableDrop.cableNombre} 
                            {puerto.cableDrop.fibraNum && ` H${puerto.cableDrop.fibraNum}`}
                          </span>
                        )}
                        {puerto.clienteNombre && (
                          <>
                            <span>→</span>
                            <span className="bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">
                              🏠 {puerto.clienteNombre}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })()}
      </div>

      {/* Resumen */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
        <h4 className="font-medium mb-2">Resumen de la NAP</h4>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Libres: {puertosLibres}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Ocupados: {puertosOcupados}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span>Reservados: {localConfig.puertos.filter(p => p.estado === 'reservado').length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>Dañados: {localConfig.puertos.filter(p => p.estado === 'dañado').length}</span>
          </div>
        </div>
        {localConfig.splitters.length > 0 && (
          <div className="mt-2 pt-2 border-t text-sm text-orange-600">
            Atenuación total por splitters: {localConfig.splitters.reduce((acc, s) => acc + s.atenuacion, 0).toFixed(1)} dB
          </div>
        )}
      </div>

      {/* Modal Editor de Salidas del Splitter */}
      {showSplitterEditor && editingSplitter && (
        <SplitterConnectionEditor
          key={editingSplitter.id}
          isOpen={showSplitterEditor}
          onClose={() => {
            setShowSplitterEditor(false)
            setEditingSplitterId(null)
          }}
          splitterId={editingSplitter.id}
          splitterRatio={editingSplitter.tipoSplitter === 'desbalanceado' ? (editingSplitter.ratioDesbalanceado || '80/20') : editingSplitter.ratio}
          tipoSplitter={editingSplitter.tipoSplitter}
          salidas={editingSplitter.salidas?.map(s => ({
            puerto: s.puerto,
            colorPigtail: COLORES_TIA598[(s.puerto - 1) % 12].codigo,
            estado: s.estado,
            destino: s.destino ? {
              tipo: s.destino.tipo,
              cableId: s.destino.cableId,
              cableNombre: s.destino.cableNombre,
              bufferNum: s.destino.bufferNum,
              fibraNum: s.destino.fibraNum,
              fibraColor: s.destino.fibraColor,
              fibraId: s.destino.cableId ? `${s.destino.cableId}|${s.destino.bufferNum || 'SB'}|${s.destino.fibraColor}` : undefined,
              puertoNAP: s.destino.puertoNAP,
              splitterDestinoId: s.destino.splitterDestinoId,
            } : undefined
          })) || []}
          cablesDisponibles={cablesConectados}
          puertosNAP={localConfig.puertos.map(p => ({ numero: p.numero, estado: p.estado }))}
          onSave={(salidas) => handleSaveSplitterSalidas(editingSplitter.id, salidas)}
          isNAP2doNivel={true}
          splittersDisponibles={localConfig.splitters
            .filter(s => s.id !== editingSplitter.id)
            .map((s, idx) => ({
              id: s.id,
              ratio: s.tipoSplitter === 'desbalanceado' ? (s.ratioDesbalanceado || '80/20') : s.ratio,
              nombre: `Splitter ${idx + 1}`,
              entradaConfigurada: !!s.entrada?.splitterOrigenId
            }))}
        />
      )}
    </div>
  )
}
